import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import Header from "../../Header";
import Sidebar from "../../Sidebar";
import Footer from "../../Footer";
import Spinner from "../../common/Spinner";
import { createApp } from "../../../redux/actions/AppActions";
import { UserDropdown } from "../../../redux/actions/userActions";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Chip from "@mui/material/Chip";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UploadFileIcon from "@mui/icons-material/UploadFile";

const AddApp = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const organization = useSelector((s) => s?.organizationDetails?.organization);
    const userRole = organization?.user_role;
    const users = useSelector((s) => s.userDropdown.users);
    const role = useSelector((s) => s.userLogin.userInfo.data.role);
    const {
        userLogin: { userInfo: { data } },
    } = useSelector((s) => s);
    const organizationId = organization?.organization_id;

    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [logo, setLogo] = useState(null);
    const [logoError, setLogoError] = useState(null);
    const [selectedUserIds, setSelectedUserIds] = useState(() => {
        const defaultUser = users.find((user) => user.name === data.name);
        return defaultUser ? [defaultUser.id] : [];
    });
    const [formState, setFormState] = useState({
        values: {
            ios: false,
            android: false,
            packageName: "",
            bundleName: "",
        },
        errors: {},
    });

    useEffect(() => {
        dispatch(UserDropdown("app", "", organizationId));
    }, [organizationId, dispatch]);

    const handleUserChange = (event) => {
        const selectedUserId = parseInt(event.target.value, 10);
        if (!Number.isNaN(selectedUserId) && !selectedUserIds.includes(selectedUserId)) {
            setSelectedUserIds([...selectedUserIds, selectedUserId]);
        }
    };

    const removeUser = (userIdToRemove) => {
        setSelectedUserIds(selectedUserIds?.filter((uid) => uid !== userIdToRemove));
    };

    const filteredOptions = users?.filter((u) => !selectedUserIds.includes(u.id)) || [];
    const selectedUsers = users?.filter((u) => selectedUserIds.includes(u.id)) || [];

    const handleUploadChange = (event) => {
        event.preventDefault();
        const file = event.target.files[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            setLogoError("Logo must be an image file.");
            setLogo(null);
            return;
        }
        const img = new Image();
        img.onload = () => {
            setLogoError(null);
            setLogo(file);
        };
        img.onerror = () => {
            setLogoError("Could not read the image. Please pick another file.");
            setLogo(null);
        };
        img.src = URL.createObjectURL(file);
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;

        if (name === "name") {
            const regex = /^[a-zA-Z0-9\s]*$/;
            if (!regex.test(value)) return;
        }

        setFormState((prev) => ({
            ...prev,
            values: {
                ...prev.values,
                [name]: type === "checkbox" ? checked : value,
            },
            errors: {
                ...prev.errors,
                [name]: getValidationError(name, value),
            },
        }));
    };

    const getValidationError = (name, value) => {
        switch (name) {
            case "logo":
                return !value ? "Logo is required" : "";
            case "ios":
            case "android":
                return !(formState.values.ios || formState.values.android)
                    ? "At least one platform must be selected"
                    : "";
            default:
                return "";
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
        const { name, description, android, ios, packageName, bundleName } = formState.values;

        if ((name && description && logo && bundleName) || packageName) {
            if (formState.values.ios || formState.values.android) {
                let _errorFlag = false, _errorMessage = "";
                if (android === true && packageName.length === 0 && ios === false) {
                    _errorFlag = true;
                    _errorMessage = "Please enter the Android package name.";
                } else if (ios === true && bundleName.length === 0 && android === false) {
                    _errorFlag = true;
                    _errorMessage = "Please enter the iOS bundle id.";
                } else if (android === true && ios === true) {
                    if (bundleName.length === 0 && packageName.length === 0) {
                        _errorFlag = true;
                        _errorMessage = "Please enter both the iOS bundle id and Android package name.";
                    } else if (packageName.length === 0) {
                        _errorFlag = true;
                        _errorMessage = "Please enter the Android package name.";
                    } else if (bundleName.length === 0) {
                        _errorFlag = true;
                        _errorMessage = "Please enter the iOS bundle id.";
                    }
                }

                if (_errorFlag === false) {
                    setLoading(true);
                    const payload = new FormData();
                    payload.set("name", name);
                    payload.set("description", description);
                    payload.set("isAndriod", android);
                    payload.set("isIos", ios);
                    payload.set("selectedUserIds", selectedUserIds);
                    payload.set("packageName", packageName);
                    payload.set("bundleName", bundleName);
                    payload.set("organization_id", organizationId);

                    if (userRole === "Member" || userRole === "Tester") {
                        payload.set("app_status", "Requested");
                    } else if (role === "Super_Admin") {
                        payload.set("app_status", "Active");
                    }
                    payload.append("logo", logo);

                    dispatch(createApp(payload)).then(() => {
                        setSubmitted(false);
                        setLoading(false);
                        navigateToAppList();
                    });
                } else {
                    toast.error(_errorMessage);
                }
            } else {
                toast.error("At least one OS must be selected");
            }
        } else if (!formState.values.name && !formState.values.description && !logo) {
            toast.error("Please fill in all the details.");
        } else if (!formState.values.name) {
            toast.error("Please enter a name.");
        } else if (!formState.values.description) {
            toast.error("Please enter a description.");
        } else if (!logo) {
            toast.error("Please upload a logo.");
        }
    };

    const navigateToAppList = () => history.push("/apps");

    const isRequestFlow = userRole === "Member" || userRole === "Tester";
    const pageTitle = isRequestFlow ? "Request App" : "Add App";

    return (
        <>
            <div className="container-scroller">
                <Header />
                <div className="container-fluid page-body-wrapper">
                    <Sidebar />
                    <Box component="main" className="main-panel" sx={{ display: "flex", flexDirection: "column" }}>
                        <Box sx={{ flex: 1, py: { xs: 2, md: 3 } }}>
                            <Container maxWidth="lg" disableGutters sx={{ px: { xs: 2, md: 3 } }}>
                                <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, border: "1px solid", borderColor: "divider" }}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                                        <IconButton component={RouterLink} to="/apps" size="small" aria-label="Back">
                                            <ArrowBackIcon />
                                        </IconButton>
                                        <Typography variant="h3" component="h1">{pageTitle}</Typography>
                                    </Stack>

                                    <form onSubmit={handleSubmit} noValidate>
                                        <Stack spacing={3}>
                                            <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
                                                <TextField
                                                    label="App name"
                                                    name="name"
                                                    required
                                                    fullWidth
                                                    value={formState.values.name || ""}
                                                    onChange={handleChange}
                                                    error={submitted && !formState.values.name}
                                                    helperText={submitted && !formState.values.name ? "App name is required" : " "}
                                                />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Button
                                                        component="label"
                                                        variant="outlined"
                                                        fullWidth
                                                        startIcon={<UploadFileIcon />}
                                                        sx={{
                                                            height: 56,
                                                            borderColor: (submitted && !logo) ? "error.main" : "divider",
                                                            color: "text.secondary",
                                                            justifyContent: "flex-start",
                                                            textTransform: "none",
                                                            minWidth: 0,
                                                            "& .MuiButton-startIcon": { flexShrink: 0 },
                                                        }}
                                                    >
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                flex: 1,
                                                                minWidth: 0,
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                                textAlign: "left",
                                                            }}
                                                            title={logo ? logo.name : undefined}
                                                        >
                                                            {logo ? logo.name : "Upload app logo *"}
                                                        </Box>
                                                        <input type="file" name="file" accept="image/*" hidden onChange={handleUploadChange} />
                                                    </Button>
                                                    {submitted && !logo && (
                                                        <FormHelperText error>Logo is required</FormHelperText>
                                                    )}
                                                    {logoError?.length > 0 && (
                                                        <FormHelperText error>{logoError}</FormHelperText>
                                                    )}
                                                    {!submitted && !logoError && (
                                                        <FormHelperText> </FormHelperText>
                                                    )}
                                                </Box>
                                            </Stack>

                                            <TextField
                                                label="Description"
                                                name="description"
                                                required
                                                fullWidth
                                                multiline
                                                rows={4}
                                                value={formState.values.description || ""}
                                                onChange={handleChange}
                                                error={submitted && !formState.values.description}
                                                helperText={submitted && !formState.values.description ? "Description is required" : " "}
                                            />

                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}>
                                                    Platform <Box component="span" sx={{ color: "error.main" }}>*</Box>
                                                </Typography>
                                                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                                                    <FormControlLabel
                                                        control={<Checkbox name="ios" checked={!!formState.values.ios} onChange={handleChange} />}
                                                        label={
                                                            <Stack direction="row" spacing={0.75} alignItems="center">
                                                                <AppleIcon fontSize="small" />
                                                                <span>iOS</span>
                                                            </Stack>
                                                        }
                                                        sx={{ minWidth: 120 }}
                                                    />
                                                    {formState.values.ios === true && (
                                                        <TextField
                                                            size="small"
                                                            name="bundleName"
                                                            placeholder="Bundle ID (com.example.app)"
                                                            value={formState.values.bundleName || ""}
                                                            onChange={handleChange}
                                                            sx={{ flex: 1, minWidth: 240 }}
                                                        />
                                                    )}
                                                </Stack>
                                                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} sx={{ mt: 1 }}>
                                                    <FormControlLabel
                                                        control={<Checkbox name="android" checked={!!formState.values.android} onChange={handleChange} />}
                                                        label={
                                                            <Stack direction="row" spacing={0.75} alignItems="center">
                                                                <AndroidIcon fontSize="small" />
                                                                <span>Android</span>
                                                            </Stack>
                                                        }
                                                        sx={{ minWidth: 120 }}
                                                    />
                                                    {formState.values.android === true && (
                                                        <TextField
                                                            size="small"
                                                            name="packageName"
                                                            placeholder="Package name (com.example.app)"
                                                            value={formState.values.packageName || ""}
                                                            onChange={handleChange}
                                                            sx={{ flex: 1, minWidth: 240 }}
                                                        />
                                                    )}
                                                </Stack>
                                                {submitted && !(formState.values.ios || formState.values.android) && (
                                                    <FormHelperText error>At least one platform must be selected</FormHelperText>
                                                )}
                                            </Box>

                                            <FormControl fullWidth>
                                                <InputLabel id="access-to-label" shrink>Access to</InputLabel>
                                                <Select
                                                    labelId="access-to-label"
                                                    label="Access to"
                                                    value=""
                                                    displayEmpty
                                                    onChange={handleUserChange}
                                                    renderValue={() =>
                                                        selectedUsers.length ? (
                                                            <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
                                                                {selectedUsers.map((user) => (
                                                                    <Chip
                                                                        key={user.id}
                                                                        label={user.name}
                                                                        onDelete={() => removeUser(user.id)}
                                                                        size="small"
                                                                        sx={{ bgcolor: "primary.main", color: "secondary.dark", fontWeight: 600 }}
                                                                    />
                                                                ))}
                                                            </Stack>
                                                        ) : <Box sx={{ color: "text.secondary" }}>Select a user to grant access</Box>
                                                    }
                                                >
                                                    <MenuItem value="" disabled>Select user</MenuItem>
                                                    {filteredOptions.map((user) => (
                                                        <MenuItem key={user.id} value={user.id}>{user.name}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ pt: 1 }}>
                                                <Button type="submit" variant="contained" size="large" sx={{ minWidth: 140 }}>
                                                    {isRequestFlow ? "Submit request" : "Add app"}
                                                </Button>
                                                <Button component={RouterLink} to="/apps" variant="outlined" size="large" sx={{ minWidth: 140 }}>
                                                    Cancel
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </form>
                                </Paper>
                            </Container>
                        </Box>
                        <Footer />
                    </Box>
                </div>
            </div>
            {loading && <Spinner fullscreen label="Saving…" />}
        </>
    );
};

export default AddApp;
